import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import FormRow from "../../ui/FormRow";
import { useCreateUser } from "./useCreateUser";
import PasswordToggle from "../../ui/PasswordToggle";
import { calculatePasswordStrength } from "../../helper/passwordStrength";
import { motion } from "framer-motion";
import { startOAuth } from "../../services/apiOAuth";
import SocialAuthButtons from "../../ui/SocialAuthButtons";
import Spinner from "../../ui/Spinner";

const SignupForm = () => {
  const navigate = useNavigate();
  const { createUser, isCreating } = useCreateUser();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [socialProvider, setSocialProvider] = useState("");
  
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    getValues,
  } = useForm({
    mode: "onChange", // Enable real-time validation
  });

  const nameValue = watch("name");
  const emailValue = watch("email");
  const passwordValue = watch("password");
  const confirmPasswordValue = watch("confirmPassword");

  // Calculate password strength in real-time
  const passwordStrength = calculatePasswordStrength(passwordValue);

  // Real-time validation states
  const isNameValid = nameValue && !errors.name && nameValue.length >= 2;
  const isEmailValid = 
    emailValue && 
    !errors.email && 
    /\S+@\S+\.\S+/.test(emailValue);
  
  const isPasswordValid = 
    passwordValue && 
    !errors.password && 
    passwordValue.length >= 6;
  
  const isConfirmPasswordValid = 
    confirmPasswordValue && 
    !errors.confirmPassword && 
    confirmPasswordValue === passwordValue;

  const onSubmit = ({ name, email, password }) => {
    createUser(
      { name, email, password },
      {
        onSuccess: () => {
          reset();
          navigate("/", { replace: true });
        },
      },
    );
  };

  const onError = (err) => {
    console.log(err);
  };

  const handleSocialAuth = (provider) => {
    if (isCreating || socialProvider) return;

    try {
      setSocialProvider(provider);
      startOAuth(provider);
    } catch (error) {
      console.error(error);
      setSocialProvider("");
    }
  };

  const isSocialLoading = Boolean(socialProvider);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="glass-panel w-full max-w-xl rounded-2xl p-8 backdrop-blur-xl"
    >
      <div className="mb-8 text-center">
        <h2 className="text-3xl font-bold text-gray-800 dark:text-white">
          Create Account
        </h2>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Sign up to get started with your portfolio
        </p>
      </div>

      {/* Social Login Buttons (Visual Only) */}
      <SocialAuthButtons disabled={isCreating || isSocialLoading} onSelect={handleSocialAuth} />

      <div className="mb-6 flex items-center justify-between">
        <span className="h-px w-full bg-gray-200 dark:bg-gray-700"></span>
        <span className="px-3 text-xs text-gray-400 uppercase">Or</span>
        <span className="h-px w-full bg-gray-200 dark:bg-gray-700"></span>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit, onError)}
        className="grid grid-cols-1 gap-y-6"
      >
        <FormRow
          label="Full Name"
          error={errors?.name?.message}
          isValid={isNameValid}
          hint={nameValue && nameValue.length > 0 && nameValue.length < 2 ? "Name must be at least 2 characters" : null}
        >
          <input
            type="text"
            placeholder="Enter your full name"
            autoComplete="name"
            className={`w-full rounded-lg border bg-gray-50 p-4 text-sm outline-none transition-all focus:ring-2 dark:bg-[#0f172a] dark:text-white ${
              errors?.name?.message
                ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                : isNameValid
                ? "border-green-500 focus:border-green-500 focus:ring-green-500/20"
                : "border-gray-200 focus:border-[#6366f1] focus:ring-[#6366f1]/20 dark:border-gray-700"
            }`}
            {...register("name", {
              required: "Name is required",
              minLength: {
                value: 2,
                message: "Name must be at least 2 characters",
              },
              maxLength: {
                value: 50,
                message: "Name must be less than 50 characters",
              },
            })}
            disabled={isCreating}
          />
        </FormRow>

        <FormRow
          label="Email"
          error={errors?.email?.message}
          isValid={isEmailValid}
        >
          <input
            type="email"
            placeholder="Enter your email"
            autoComplete="email"
            className={`w-full rounded-lg border bg-gray-50 p-4 text-sm outline-none transition-all focus:ring-2 dark:bg-[#0f172a] dark:text-white ${
              errors?.email?.message
                ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                : isEmailValid
                ? "border-green-500 focus:border-green-500 focus:ring-green-500/20"
                : "border-gray-200 focus:border-[#6366f1] focus:ring-[#6366f1]/20 dark:border-gray-700"
            }`}
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: "Please provide a valid email address",
              },
            })}
            disabled={isCreating}
          />
        </FormRow>

        <FormRow
          label="Password"
          error={errors?.password?.message}
          isValid={isPasswordValid && passwordStrength.strength >= 3}
        >
          <div className="space-y-2">
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                autoComplete="new-password"
                className={`w-full rounded-lg border bg-gray-50 p-4 pr-12 text-sm outline-none transition-all focus:ring-2 dark:bg-[#0f172a] dark:text-white ${
                  errors?.password?.message
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                    : isPasswordValid && passwordStrength.strength >= 3
                    ? "border-green-500 focus:border-green-500 focus:ring-green-500/20"
                    : "border-gray-200 focus:border-[#6366f1] focus:ring-[#6366f1]/20 dark:border-gray-700"
                }`}
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters long",
                  },
                })}
                disabled={isCreating}
              />
              <PasswordToggle
                show={showPassword}
                onToggle={() => setShowPassword(!showPassword)}
              />
            </div>
            {/* Password Strength Indicator Visuals */}
            {passwordValue && passwordValue.length > 0 && (
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${passwordStrength.percentage}%` }}
                    className={`h-full ${
                      passwordStrength.color === "red"
                        ? "bg-red-500"
                        : passwordStrength.color === "orange"
                        ? "bg-orange-500"
                        : passwordStrength.color === "yellow"
                        ? "bg-yellow-500"
                        : passwordStrength.color === "blue"
                        ? "bg-blue-500"
                        : "bg-green-500"
                    }`}
                  />
                </div>
            )}
          </div>
        </FormRow>

        <FormRow
          label="Confirm Password"
          error={errors?.confirmPassword?.message}
          isValid={isConfirmPasswordValid}
        >
          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm your password"
              autoComplete="new-password"
              className={`w-full rounded-lg border bg-gray-50 p-4 pr-12 text-sm outline-none transition-all focus:ring-2 dark:bg-[#0f172a] dark:text-white ${
                errors?.confirmPassword?.message
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                  : isConfirmPasswordValid
                  ? "border-green-500 focus:border-green-500 focus:ring-green-500/20"
                  : "border-gray-200 focus:border-[#6366f1] focus:ring-[#6366f1]/20 dark:border-gray-700"
              }`}
              {...register("confirmPassword", {
                required: "Please confirm your password",
                validate: (value) =>
                  value === getValues().password || "Passwords do not match!",
              })}
              disabled={isCreating}
            />
            <PasswordToggle
              show={showConfirmPassword}
              onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
            />
          </div>
        </FormRow>

        <button
          disabled={isCreating || !isNameValid || !isEmailValid || !isPasswordValid || !isConfirmPasswordValid}
          type="submit"
          className="w-full rounded-lg bg-[#6366f1] py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#6366f1]/30 transition-all hover:scale-[1.02] hover:shadow-xl hover:shadow-[#6366f1]/40 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:scale-100"
        >
          {isCreating ? (
            <span className="flex items-center justify-center gap-2">
              <Spinner />
              Creating Account...
            </span>
          ) : (
            "Create Account"
          )}
        </button>
      </form>

      <p className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
        Already have an account?{" "}
        <Link
          className="font-medium text-[#6366f1] hover:text-[#818cf8] hover:underline transition-colors"
          to="/login"
        >
          Sign in
        </Link>
      </p>
    </motion.div>
  );
};

export default SignupForm;
